package com.example.mystybag.common.ui.views

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.ScrollableState
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.asPaddingValues
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBars
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.BottomSheetScaffold
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.SheetValue
import androidx.compose.material3.rememberBottomSheetScaffoldState
import androidx.compose.material3.rememberStandardBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.blur
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.zIndex
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.common.expected.BackHandler
import com.example.mystybag.common.expected.setWebBottomSheetOpen
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.extensions.modifyIf
import com.example.mystybag.common.extensions.noRippleClickable
import com.example.mystybag.core.logic.base.AppViewModel.Companion.baseBottomSheetDidClose
import com.example.mystybag.core.logic.base.BottomSheetChild
import com.example.mystybag.core.logic.helpers.UIBottomSheetControl
import com.example.mystybag.common.ui.components.custom.ProgressNotch
import com.example.mystybag.common.ui.components.custom.StateProgressView
import com.example.mystybag.core.expected.composeColor
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.ui.theme.LocalTheme.p0
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.views.AppTheme
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

data class LocalScrollState(
    var state: MutableState<ScrollableState?> = mutableStateOf(null)
)

val LocalScrollableStateProvider = compositionLocalOf {
    LocalScrollState()
}

val LocalProgressProvider = compositionLocalOf {
    true
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun BottomSheetLayoutView(
    sheetControl                : UIBottomSheetControl,
    modifier                    : Modifier = Modifier,
    removeBottomSheetPadding    : Boolean                                   = false,
    overrideChildProgress       : Boolean                                   = true,
    bottomSheetFactory          : @Composable (BottomSheetChild) -> Unit,
    screenContent               : @Composable () -> Unit,
) {
    val baseUIState = sheetControl.baseUIState.subscribeAsState()
    val bottomSheet = sheetControl.sheetState.subscribeAsState()
    val keyboard    = LocalSoftwareKeyboardController.current
    val canClose    = remember { mutableStateOf(true) }
    val bsIsOpen    = remember { mutableStateOf(false) }
    val bsChild     = remember { mutableStateOf(bottomSheet.value.child?.instance) }
    val applyBlur   = remember { mutableStateOf(false) }

    val scope       = rememberCoroutineScope()
    val bottomPadding = if (removeBottomSheetPadding || bottomSheet.value.child?.instance?.removeBottomSheetPadding == true) p0 else WindowInsets.navigationBars.asPaddingValues().calculateBottomPadding()
    val useWebDialogInsets = PlatformHelper.isWeb && bottomSheet.value.child?.instance?.useWebDialogInsets == true
    val webVerticalInset = if (useWebDialogInsets) 48.dp else p0

    val sheetState = rememberBottomSheetScaffoldState(
        bottomSheetState = rememberStandardBottomSheetState(
            initialValue        = SheetValue.PartiallyExpanded,
            skipHiddenState     = false,
            confirmValueChange = { value ->
                if (canClose.value && value == SheetValue.PartiallyExpanded)
                    keyboard?.hide()

                (canClose.value && value == SheetValue.PartiallyExpanded) || value == SheetValue.Expanded
            }
        )
    )

    LaunchedEffect(Unit) {
        snapshotFlow { sheetState.bottomSheetState.currentValue }
            .collect { ss ->
                if (ss == SheetValue.PartiallyExpanded && bsIsOpen.value)
                    sheetControl.bottomSheetDidClose(bsChild.value)

                bsIsOpen.value = (ss == SheetValue.Expanded)
            }
    }

    BackHandler(baseUIState.value.bottomSheetIsOpen) {
        if (!baseBottomSheetDidClose()) {
            if (canClose.value && !baseUIState.value.showProgress && sheetState.bottomSheetState.currentValue == SheetValue.Expanded)
                sheetControl.closeBottomSheet()
        }
    }

    LaunchedEffect(baseUIState.value.bottomSheetIsOpen) {
        setWebBottomSheetOpen(baseUIState.value.bottomSheetIsOpen)
        if (baseUIState.value.bottomSheetIsOpen) {
            bsIsOpen.value = true
            delay(25)
            sheetState.bottomSheetState.expand()
        }
        else if (sheetState.bottomSheetState.currentValue == SheetValue.Expanded) {
            canClose.value = true
            sheetState.bottomSheetState.partialExpand()
        }
    }

    LaunchedEffect(bottomSheet.value.child?.instance) {
        canClose.value = bottomSheet.value.child?.instance?.canClose != false
    }

    LaunchedEffect(bsIsOpen.value) {
        if (bsIsOpen.value)
            applyBlur.value = true
        else {
            delay(50)
            applyBlur.value = false
        }
    }

    CompositionLocalProvider(LocalScrollableStateProvider provides LocalScrollState()) {

        Box(modifier = Modifier.fillMaxSize()) {

            BottomSheetScaffold(
                modifier                = modifier,
                scaffoldState           = sheetState,
                sheetPeekHeight         = p0,
                sheetSwipeEnabled       = canClose.value && baseUIState.value.bottomSheetIsOpen && (LocalScrollableStateProvider.current.state.value?.canScrollBackward != true),
                sheetDragHandle         = null,
                sheetShape              = RoundedCornerShape(topStart = p20, topEnd = p20),
                sheetTonalElevation     = p0,
                sheetShadowElevation    = p0,
                sheetContainerColor     = if (useWebDialogInsets) Color.Transparent else AppTheme.current.colors.surface,
                containerColor          = AppTheme.current.colors.background.copy(alpha = 0f),
                sheetContent            = {
                    bottomSheet.value.child?.instance?.let {
                        bsChild.value = it

                        if (useWebDialogInsets) {
                            Box(
                                modifier = Modifier
                                    .zIndex(2f)
                                    .fillMaxWidth()
                                    .fillMaxHeight()
                                    .padding(top = webVerticalInset),
                                contentAlignment = Alignment.TopCenter
                            ) {
                                Column(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .wrapContentHeight(align = Alignment.Top)
                                        .clip(RoundedCornerShape(24.dp))
                                        .background(AppTheme.current.colors.surface)
                                        .padding(bottom = bottomPadding)
                                ) {
                                    if (it.showNotch) {
                                        ProgressNotch(
                                            modifier = Modifier.align(Alignment.CenterHorizontally),
                                            onTimeout = it.onTimeout
                                        )
                                    }
                                    bottomSheetFactory(it)
                                }
                            }
                        } else {
                            Column(
                                modifier = Modifier
                                    .zIndex(2f)
                                    .fillMaxWidth()
                                    .padding(top = webVerticalInset, bottom = webVerticalInset)
                                    .clip(RoundedCornerShape(0.dp))
                                    .background(AppTheme.current.colors.surface)
                                    .padding(bottom = bottomPadding)
                            ) {
                                if (it.showNotch) {
                                    ProgressNotch(
                                        modifier = Modifier.align(Alignment.CenterHorizontally),
                                        onTimeout = it.onTimeout
                                    )
                                }
                                bottomSheetFactory(it)
                            }
                        }
                    }
                }
            ) {
                Box(modifier = Modifier
                    .fillMaxSize()
                ) {
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .zIndex(0f)
                            .padding(it)
                            .modifyIf(applyBlur.value) { blur(radiusX = p8, radiusY = p8) }
                    ) {
                        if (overrideChildProgress)
                            CompositionLocalProvider(LocalProgressProvider provides false) {
                                screenContent()
                            }
                        else
                            screenContent()
                    }

                    AnimatedVisibility(
                        visible = applyBlur.value,
                        enter   = fadeIn(animationSpec  = tween(durationMillis = 350)),
                        exit    = fadeOut(animationSpec = tween(durationMillis = 350))
                    ) {
                        Box(modifier = Modifier
                            .fillMaxSize()
                            .zIndex(1f)
                            .background(SR.colors.background_black.composeColor.copy(alpha = 0.5f))
                            .noRippleClickable(remember { {
                                if (canClose.value && sheetState.bottomSheetState.currentValue == SheetValue.Expanded) {
                                    scope.launch {
                                        sheetState.bottomSheetState.partialExpand()
                                    }
                                }
                            } })
                        )
                    }
                }
            }

            StateProgressView(visible = baseUIState.value.showProgress)
        }
    }
}